/**
 * Minimal markdown renderer for agent answers — tables, bullet lists, inline
 * bold/code, paragraphs. Ported from the original POC's frontend
 * (query-assistant-agent-ha-2026/frontend/index.html), which had this and
 * our panel didn't — answers with tables or bullets were showing as raw
 * literal asterisks/pipes before this.
 *
 * Security note: every raw text value is escaped via `escapeHtml` BEFORE any
 * formatting markup is applied — the only unescaped HTML ever introduced is
 * the small set of tags this module inserts itself (<strong>, <code>, <table>
 * etc.). This must stay in that order; formatting-before-escaping would be
 * an XSS hole since answers ultimately originate from an LLM.
 */

export function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

export function splitTableRow(line) {
  let l = line.trim();
  if (l.startsWith("|")) l = l.slice(1);
  if (l.endsWith("|")) l = l.slice(0, -1);
  return l.split("|").map((c) => c.trim());
}

export function isTableSeparator(line) {
  // The repeated group is (\|\s*:?-{2,}:?\s*)* -- zero or more, not one or
  // more. A one-or-more repeated group means every separator row needed a
  // SECOND "|---" column beyond the first to match at all, so a genuine
  // one-column table's separator ("|---|") was never recognized as a table
  // separator -- the whole table (header + rows too, since detection keys
  // off this) fell through to literal paragraph text instead. Confirmed
  // against a real answer: a one-column "Requisitions with No Additional
  // Details" table rendered as raw "| TEMP1 |" lines while a multi-column
  // table earlier in the SAME message rendered correctly.
  return /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/.test(line.trim());
}

function inlineFormat(s) {
  let h = escapeHtml(s);
  // Bold (**text**) MUST run before italic (*text*) below -- otherwise the
  // italic regex would consume one asterisk of each ** pair first and
  // leave the rest as stray literal asterisks. Once bold's replace has run,
  // no ** sequences remain in well-formed input, so the italic pass only
  // ever sees genuine single-asterisk spans.
  //
  // ADDED: single-asterisk italic. Copilot Studio answers use *(not
  // available)*, *(no status)*, *(not recorded)*, and *(date)* very
  // consistently as its placeholder/aside convention -- this file only
  // ever handled ** and `code`, so every one of those spans was rendering
  // as literal text with the asterisks still showing (confirmed against a
  // raw API response: "*(not available)*" in the JSON, "*(not available)*"
  // -- asterisks and all -- in the rendered table). [^*\n] keeps a match
  // from crossing a newline or swallowing a second, unrelated single-star
  // span in the same line.
  h = h
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*\n]+?)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>");
  h = linkify(h);
  return h;
}

/**
 * Turns raw URLs, markdown-style [label](url) links, and literal HTML
 * anchor tags into real anchor tags. Runs on already-escaped HTML (see
 * inlineFormat) — never call this on raw, unescaped text. Answers were
 * showing URLs (e.g. SharePoint OG Checklist links) as inert plain text
 * because nothing in this file previously handled links at all, only
 * **bold** and `code`.
 *
 * Trailing punctuation (., ,, ), ], etc.) commonly ends the sentence rather
 * than the URL itself — e.g. "...see the doc (https://example.com)." — so
 * it's stripped from the href/link text and placed back after the tag.
 */
function linkify(html) {
  // Each pass below can insert a real <a href="...">...</a> tag into the
  // string -- but every pass scans the WHOLE string, so a later pass would
  // otherwise find the URL that's now sitting inside an already-built
  // href="..." attribute and wrap it AGAIN, corrupting the tag (confirmed
  // by testing: this bug existed even before this file gained the raw-
  // anchor-tag case below, purely between the markdown-link pass and the
  // bare-URL pass). Fix: never insert real HTML mid-function -- insert a
  // one-off placeholder token instead, and substitute all tokens back to
  // real HTML in a single final pass once every regex above has already
  // run. Null bytes can't appear in escaped HTML text and can't collide
  // with any pattern used below, so they're safe as token delimiters.
  const placeholders = [];
  const store = (fragment) => {
    const token = `\u0000LINK${placeholders.length}\u0000`;
    placeholders.push(fragment);
    return token;
  };

  // The agent sometimes emits a literal HTML <a href="...">label</a> tag
  // as text (e.g. the OG Checklist link) rather than markdown syntax.
  // escapeHtml (correctly) already turned that tag's own < and > into
  // &lt;/&gt;, but does NOT escape quote characters -- so without this
  // step, the bare-URL pass below would partially match just the href
  // value up to its closing quote, and everything after it (the rest of
  // the tag's attributes and the label) would render as literal escaped
  // text instead of the link's visible label. This must run BEFORE the
  // bare-URL pass so that pass never sees a partial tag to mangle.
  // Supports either quote style and any attribute order/count between
  // href and the closing >; only http(s) hrefs are honored (this is a
  // sanitizing re-emit, not a passthrough of arbitrary agent-supplied
  // HTML -- the label is HTML-escaped text at this point, so it's safe
  // to place directly, but the href itself is re-quoted defensively in
  // case it contains a stray literal quote character).
  html = html.replace(
    /&lt;a\s+href=(?:"([^"]+)"|'([^']+)')[\s\S]*?&gt;([\s\S]*?)&lt;\/a&gt;/gi,
    (match, hrefDouble, hrefSingle, label) => {
      const href = hrefDouble || hrefSingle || "";
      if (!/^https?:\/\//i.test(href)) return match;
      const safeHref = href.replace(/"/g, "&quot;");
      return store(`<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${label}</a>`);
    }
  );

  // Markdown-style [label](url) next, so the raw-URL pass below doesn't
  // also wrap the same URL a second time inside the parens.
  html = html.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (match, label, url) => store(`<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`)
  );

  html = html.replace(/(https?:\/\/[^\s<>"']+)/g, (match) => {
    const trailing = match.match(/[.,;:!?)\]]+$/);
    const url = trailing ? match.slice(0, -trailing[0].length) : match;
    const trailingText = trailing ? trailing[0] : "";
    if (!url) return match;
    return store(`<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`) + trailingText;
  });

  return html.replace(/\u0000LINK(\d+)\u0000/g, (_, idx) => placeholders[Number(idx)]);
}

let tableIdCounter = 0;

function renderTableBlock(lines) {
  const header = splitTableRow(lines[0]);
  const bodyRows = lines.slice(2).map(splitTableRow);
  const thead = "<tr>" + header.map((c) => `<th>${inlineFormat(c)}</th>`).join("") + "</tr>";
  const tbody = bodyRows
    .map((r) => "<tr>" + r.map((c) => `<td>${inlineFormat(c)}</td>`).join("") + "</tr>")
    .join("");
  // Unique id per table so the panel's window-registered click handlers
  // (see CopilotChatPanel.js) know which table's DOM to read back from --
  // Copilot Studio answers commonly contain several tables in one message
  // (e.g. "Overview" + "Personnel" + "Class Fill Info" as separate tables),
  // so per-table controls need to target one specific table, not "the
  // first table in the message" the way the old single message-level copy/
  // export buttons did.
  const tableId = `mdtbl-${++tableIdCounter}`;
  const actions =
    `<div class="mdTableActions">` +
    `<button type="button" class="mdTableActionBtn" onclick="window.__astt_copyMdTable && window.__astt_copyMdTable('${tableId}')" title="Copy table" aria-label="Copy table">` +
    `<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="11" height="11" rx="1.5"/><path d="M5 15V5a1.5 1.5 0 0 1 1.5-1.5H15"/></svg>` +
    `</button>` +
    `<button type="button" class="mdTableActionBtn" onclick="window.__astt_exportMdTable && window.__astt_exportMdTable('${tableId}')" title="Export as CSV" aria-label="Export table as CSV">` +
    `<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 3v12m0 0l-4-4m4 4l4-4M5 19h14"/></svg>` +
    `</button>` +
    `</div>`;
  // ASTT-7496 (QA #18): the actions overlay now lives OUTSIDE the
  // scrolling element -- .mdTableWrap itself no longer scrolls (that moved
  // to the new inner .mdTableScroll, which wraps only the <table>). Before
  // this, .mdTableActions was position:absolute but a DIRECT CHILD of the
  // element that had overflow-x:auto, so it scrolled along with the table
  // instead of staying pinned to the corner. Splitting the scroll region
  // from the (non-scrolling) positioning context is more robust here than
  // switching to position:sticky, which would need the button cluster
  // in-flow to compute a "stuck" offset and adds real cross-browser risk
  // for a hover-revealed overlay that has no layout role of its own.
  return (
    `<div class="mdTableWrap" data-md-table-id="${tableId}">` +
    actions +
    `<div class="mdTableScroll"><table><thead>${thead}</thead><tbody>${tbody}</tbody></table></div>` +
    `</div>`
  );
}

function renderBlock(block) {
  const lines = block.split("\n").filter((l) => l.trim().length);
  if (lines.length === 0) return "";

  // A lone "---" (or longer) line, the WHOLE block -- render as an actual
  // rule rather than literal dashes.
  if (lines.length === 1 && /^-{3,}$/.test(lines[0].trim())) {
    return '<hr class="mdHr">';
  }

  return renderLineGroup(lines);
}

/**
 * Walks a run of lines, grouping consecutive lines of the same kind into one
 * element each: a table (header+separator+rows) -> one <table>, a run of
 * bullets -> one <ul>, a run of blockquote lines (">") -> one <blockquote>
 * (with its OWN content re-processed through this same function, so a
 * heading/bullet/paragraph inside a blockquote still renders correctly --
 * e.g. "> \u26a0\ufe0f **Key Observations:**" followed by "> - " bulleted lines, a
 * pattern Copilot Studio uses often for callout-style notes), a run of plain
 * text -> one <p>. Any ATX heading line (# through ######) becomes its own
 * heading regardless of what surrounds it.
 *
 * Used both at the top level (renderBlock) and recursively for a
 * blockquote's inner lines -- kept as one shared function rather than two
 * copies so heading/bullet/table detection can never drift between the two
 * call sites.
 *
 * Table and blockquote detection both scan for their marker at ANY position
 * in the lines, not just lines[0] -- agent answers commonly don't put a
 * blank line before a heading's following content (table, bullets, or a
 * blockquote), which would otherwise land in the same \n\n-delimited block
 * as whatever came before it.
 */
function renderLineGroup(lines) {
  const htmlParts = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();

    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      htmlParts.push(
        `<h${level} class="mdHeading mdHeading${level}">${inlineFormat(headingMatch[2])}</h${level}>`
      );
      i += 1;
      continue;
    }

    if (
      /^\|.*\|?$/.test(line) &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1])
    ) {
      const tableLines = [lines[i], lines[i + 1]];
      let j = i + 2;
      while (j < lines.length && /^\|.*\|?$/.test(lines[j].trim())) {
        tableLines.push(lines[j]);
        j += 1;
      }
      htmlParts.push(renderTableBlock(tableLines));
      i = j;
      continue;
    }

    if (/^>\s?/.test(line)) {
      const quoteLines = [];
      while (i < lines.length && /^>\s?/.test(lines[i].trim())) {
        quoteLines.push(lines[i].trim().replace(/^>\s?/, ""));
        i += 1;
      }
      htmlParts.push(`<blockquote class="mdBlockquote">${renderLineGroup(quoteLines)}</blockquote>`);
      continue;
    }

    if (/^- /.test(line)) {
      const items = [];
      while (i < lines.length && /^- /.test(lines[i].trim())) {
        items.push(`<li>${inlineFormat(lines[i].trim().replace(/^- /, ""))}</li>`);
        i += 1;
      }
      htmlParts.push(`<ul>${items.join("")}</ul>`);
      continue;
    }

    const textLines = [];
    while (
      i < lines.length &&
      !/^#{1,6}\s+/.test(lines[i].trim()) &&
      !/^- /.test(lines[i].trim()) &&
      !/^>\s?/.test(lines[i].trim()) &&
      !(
        /^\|.*\|?$/.test(lines[i].trim()) &&
        i + 1 < lines.length &&
        isTableSeparator(lines[i + 1])
      )
    ) {
      textLines.push(lines[i]);
      i += 1;
    }
    htmlParts.push(`<p>${inlineFormat(textLines.join("\n")).replace(/\n/g, "<br>")}</p>`);
  }

  return htmlParts.join("");
}

/** Renders agent answer text (markdown subset) to a safe HTML string. */
export function renderMarkdown(text) {
  if (!text) return "";
  return text.split(/\n{2,}/).map(renderBlock).join("");
}

/**
 * Finds the first markdown table block within agent answer text and returns
 * its structured form, or null if there isn't one. Used by the "export CSV"
 * button — reuses the exact same table-detection this module already does
 * for rendering, so "does this message have a table" and "what does the
 * table actually contain" can never disagree with each other.
 */
export function extractTableFromMarkdown(text) {
  if (!text) return null;
  const tables = extractAllTablesFromMarkdown(text);
  return tables.length > 0 ? tables[0] : null;
}

/**
 * Finds EVERY markdown table block within agent answer text, in order.
 * Copilot Studio answers commonly include several tables in one response
 * (e.g. "Overview" + "Personnel" + "Class Fill & Trainee Info" as separate
 * tables) -- extractTableFromMarkdown alone can only ever surface the
 * first one, which was fine for the ADK agent's typically single-table
 * answers but isn't enough once per-table copy/export controls are needed.
 * extractTableFromMarkdown above is now a thin wrapper over this for
 * backward compatibility with any existing single-table caller.
 */
export function extractAllTablesFromMarkdown(text) {
  if (!text) return [];
  const blocks = text.split(/\n{2,}/);
  const tables = [];
  for (const block of blocks) {
    const lines = block.split("\n").filter((l) => l.trim().length);
    // Detect a table starting at ANY line within the block, not just
    // lines[0] -- same fix as renderBlock's table handling. A heading
    // directly followed by a table (no blank line between them) means the
    // table's header row isn't lines[0] of the block.
    let i = 0;
    while (i < lines.length) {
      const line = lines[i].trim();
      if (/^\|.*\|?$/.test(line) && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
        const headers = splitTableRow(lines[i]);
        const rows = [];
        let j = i + 2;
        while (j < lines.length && /^\|.*\|?$/.test(lines[j].trim())) {
          rows.push(splitTableRow(lines[j]));
          j += 1;
        }
        tables.push({ headers, rows });
        i = j;
      } else {
        i += 1;
      }
    }
  }
  return tables;
}
